import React, { useCallback } from 'react';
import { Box, Button, createStyles, Grid, makeStyles, Typography } from '@material-ui/core';
import { DateDisplay, EnumValueDisplay } from '@lib/common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  BulkChangeBillingRunDetail,
  BulkChangeBillingRunStatus,
  getBulkChangeBillingService,
} from 'app/state';

interface Props {
  run: BulkChangeBillingRunDetail;
}

const useStyles = makeStyles(() =>
  createStyles({
    detailRow: {
      display: 'flex',
      alignItems: 'baseline',
      marginBottom: '0.5rem',
    },
    label: {
      fontWeight: 600,
      marginRight: '0.5rem',
    },
  })
);

export const BulkChangeBillingRunSummary: React.FC<Props> = ({ run }) => {
  const styles = useStyles();

  const isCompleted =
    run.status === BulkChangeBillingRunStatus.Complete ||
    run.status === BulkChangeBillingRunStatus.PartialFailure ||
    run.status === BulkChangeBillingRunStatus.Failed;

  const handleDownload = useCallback(() => {
    if (run.id) getBulkChangeBillingService().exportRun(run.id);
  }, [run]);

  return (
    <Box mb={3}>
      <Typography variant="h1" gutterBottom>
        Bulk Change Billing Run
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Box className={styles.detailRow}>
            <Typography className={styles.label}>Status:</Typography>
            <EnumValueDisplay value={run.status} />
          </Box>
          <Box className={styles.detailRow}>
            <Typography className={styles.label}>Corporate Account:</Typography>
            <Typography>{run.corporateAccountName ?? '-'}</Typography>
          </Box>
          <Box className={styles.detailRow}>
            <Typography className={styles.label}>New Delegate:</Typography>
            <Typography>{run.newDelegateName ?? '-'}</Typography>
          </Box>
          <Box className={styles.detailRow}>
            <Typography className={styles.label}>Created:</Typography>
            {run.createdAt ? (
              <DateDisplay date={new Date(run.createdAt)} dateFormat="M/d/yy h:mm a" />
            ) : (
              <Typography>-</Typography>
            )}
          </Box>
          {run.completedAt && (
            <Box className={styles.detailRow}>
              <Typography className={styles.label}>Completed:</Typography>
              <DateDisplay date={new Date(run.completedAt)} dateFormat="M/d/yy h:mm a" />
            </Box>
          )}
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box className={styles.detailRow}>
            <Typography className={styles.label}>Total Requested:</Typography>
            <Typography>{run.totalRequested ?? 0}</Typography>
          </Box>
          <Box className={styles.detailRow}>
            <Typography className={styles.label}>Succeeded:</Typography>
            <Typography>{run.totalSucceeded ?? 0}</Typography>
          </Box>
          <Box className={styles.detailRow}>
            <Typography className={styles.label}>Failed:</Typography>
            <Typography>{run.totalFailed ?? 0}</Typography>
          </Box>
        </Grid>
      </Grid>

      {isCompleted && (
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleDownload}
            startIcon={<FontAwesomeIcon icon="download" />}
          >
            Download Report
          </Button>
        </Box>
      )}
    </Box>
  );
};
